/**
 * ADR-0229 — pure helpers for the ops2026 Agent registry view.
 * Kept out of the components so they can be unit-tested without a DOM.
 */

import type {
  AdminAgentRegistryApproveBody,
  AdminAgentRegistryCategory,
  AdminAgentRegistryProposal,
  AdminAgentRegistryWarning,
} from '@sill/types/api';
import {
  AI_KIND_CATEGORY,
  AUTO_MIN_AI_CONFIDENCE,
  SILL_INTERNAL_TOOLS,
  TRUSTED_SIGNERS,
  validateApprovedUaPattern,
  validateKvAgentId,
} from '@sill/types';

import { ApiError } from '../../api/http-client.js';

export const CATEGORY_OPTIONS: readonly { value: AdminAgentRegistryCategory; label: string }[] = [
  { value: 'ai_crawler', label: 'AI crawler' },
  { value: 'ai_assistant', label: 'AI assistant fetcher' },
  { value: 'search', label: 'Search' },
  { value: 'seo', label: 'SEO' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'social_preview', label: 'Link preview' },
  { value: 'hostile', label: 'Hostile / scanner' },
];

/** ADR-0230 — "Operator" is shown as "Company", with this one-line help. */
export const COMPANY_HELP = 'Company: who runs this bot (for example Exa, Ahrefs, UptimeRobot). Merchants see it next to the name.';

/** ADR-0230 — the one paragraph that tells bots, agents and people apart. */
export const KIND_GROUPS_HELP: readonly string[] = [
  'Agents act for a person in real time (AI assistant): someone asked an AI to open this page.',
  'Bots work on their own schedule: AI crawlers, AI search, search engines, SEO tools, monitors, feed readers and link previews.',
  'People use ordinary browsers. They are never proposed and never registered.',
  'Registering a name only labels traffic. It never marks a visit as verified; that still needs the operator\'s signature or published IP ranges.',
];

/** ADR-0230 — Sill's own tools are built in; they never show up here. */
export const SILL_INTERNAL_NOTE = `Sill's own tools (${SILL_INTERNAL_TOOLS.map((t) => t.token).join(', ')}) are built in as "Sill (internal)": never proposed, never counted as agent traffic.`;

/**
 * ADR-0229 (security review M1) — exactly what Automatic mode will and
 * will not approve. Shown on the page and in the confirm dialog; mirrors
 * the server rules in jobs/agent-registry-discovery.ts + registry-approval.ts.
 */
export const AUTO_MODE_RULES: readonly string[] = [
  'Automatic runs once a day and approves at most 50 names a day. Each one gets an Auto badge and a Revoke button below. Approving only labels traffic; it never marks a visit as verified.',
  `AI path: approves a candidate when the AI classified it with confidence ${AUTO_MIN_AI_CONFIDENCE} or more as a bot or agent (not a person, a scanner, hostile or unknown), named the company, and the company is not one Sill already knows under another name; AND it was seen by at least one established account (14+ days old, verified domain, live Worker or plugin), on 3 or more days, from 3 or more address blocks (or with 3 verified signatures), on 2 or more verified sites (or with a signature or Cloudflare-verified hit — a cross-check, not proof: these sites can share one account); AND it has no impersonation or spoof-cluster marks and passes every name and pattern check.`,
  `Signed path: a candidate signed at least 3 times by a reviewed operator signer (today: ${TRUSTED_SIGNERS.map((t) => t.host).join(', ')}) using that operator's own name, seen across 2 accounts.`,
  'Never automatic: people\'s browsers, scanners and hostile traffic (the AI suggests, you confirm), names that look like a browser, names that collide with built-in agents, anything revoked before, and anything you snoozed.',
];

export function categoryLabel(c: string | null | undefined): string {
  return CATEGORY_OPTIONS.find((o) => o.value === c)?.label ?? '—';
}

export const IMPERSONATION_LABEL: Readonly<Record<string, string>> = {
  sig_invalid: 'Signature failed',
  signer_mismatch: 'Wrong signer',
  ip_outside_published_ranges: 'Outside published IP ranges',
  robots_token_ua: 'robots.txt token used as UA',
  rotation_cluster: 'Rotation cluster',
};

export interface ApproveDraft {
  category: AdminAgentRegistryCategory;
  agent_id: string;
  display_name: string;
  operator: string;
  docs_url: string;
  pattern: string;
  ip_list_url: string;
  ip_list_format: 'google_json' | 'plain_list';
}

/**
 * Pre-fill the approve form. ADR-0230: from the AI classification when
 * there is one (category from its kind, company, product token, docs URL
 * the UA itself carries), so a reviewer usually only clicks Approve.
 */
export function draftFromProposal(p: AdminAgentRegistryProposal): ApproveDraft {
  const ai = p.ai_classification ?? null;
  const ai_category = ai !== null ? AI_KIND_CATEGORY[ai.kind] : null;
  return {
    category: (ai_category as AdminAgentRegistryCategory | null) ?? p.suggested_category ?? 'ai_crawler',
    agent_id: p.suggested_agent_id ?? '',
    display_name: ai?.product_token ?? p.display_name ?? p.product_token ?? '',
    operator: ai?.company ?? p.evidence.signer_hosts?.[0] ?? '',
    docs_url: ai?.docs_url ?? '',
    pattern: p.suggested_pattern ?? '',
    ip_list_url: '',
    ip_list_format: 'google_json',
  };
}

/** True when Automatic (either path) made the decision. */
export function isAutoDecided(p: Pick<AdminAgentRegistryProposal, 'decided_by'>): boolean {
  return p.decided_by === 'auto' || p.decided_by === 'auto_ai';
}

/**
 * Client-side mirror of the server checks, for early feedback only — the
 * API re-validates everything. Returns a list of human messages.
 */
export function draftProblems(d: ApproveDraft, sample_ua: string | null): string[] {
  const out: string[] = [];
  const id = validateKvAgentId(d.agent_id);
  if (!id.ok) {
    out.push(
      id.reason === 'bundled_id'
        ? 'That id is already a bundled agent.'
        : id.reason === 'reserved_prefix'
          ? 'Ids starting agent_sill_ are reserved.'
          : id.reason === 'test_shaped'
            ? 'Test-shaped ids (smoke, verify, test) are refused.'
            : 'Id must look like agent_name or agent_operator_name (lower case).',
    );
  }
  if (d.display_name.trim().length === 0 || d.display_name.trim().length > 80) out.push('Display name is required (80 characters max).');
  if (d.operator.trim().length === 0 || d.operator.trim().length > 120) out.push('Company is required.');
  // ADR-0230 amendment 2026-10-07: a Sill name, brand look-alikes and
  // browser / app names are server-side warnings ("Approve anyway"), not
  // problems that block the form.
  if (d.docs_url.trim().length > 0 && !/^https:\/\//.test(d.docs_url.trim())) out.push('Docs URL must start with https://');
  if (d.ip_list_url.trim().length > 0) {
    if (!/^https:\/\//.test(d.ip_list_url.trim())) out.push('IP list URL must start with https://');
    if (d.docs_url.trim().length === 0) out.push('An IP list needs the docs URL (both must be on the company\'s own domain).');
    if (d.category === 'hostile') out.push('Hostile identities do not take an IP list.');
  }
  const p = validateApprovedUaPattern(d.pattern, sample_ua ?? undefined, { allow_browser_token: true });
  if (!p.ok) {
    const why: Record<string, string> = {
      too_long: 'Pattern is too long.',
      browser_token: 'That token appears in ordinary browser user agents and cannot be registered.',
      bad_shape: 'Pattern must be \\bToken/[A-Za-z0-9._-]{1,64}, \\bToken[ /][A-Za-z0-9._-]{1,64} or \\bToken\\b.',
      redos_shape: 'Pattern has a nested quantifier.',
      does_not_compile: 'Pattern does not compile.',
      matches_browser: 'Pattern matches ordinary browsers.',
      collides_with_bundled: 'Pattern overlaps a bundled agent.',
      sample_not_matched: 'Pattern does not match the observed user agent.',
    };
    out.push(why[p.reason] ?? 'Pattern is not allowed.');
  }
  return out;
}

export function toApproveBody(d: ApproveDraft, acknowledged_warnings?: readonly string[]): AdminAgentRegistryApproveBody {
  return {
    category: d.category,
    agent_id: d.agent_id.trim(),
    display_name: d.display_name.trim(),
    operator: d.operator.trim(),
    docs_url: d.docs_url.trim().length > 0 ? d.docs_url.trim() : null,
    pattern: d.pattern,
    ...(d.ip_list_url.trim().length > 0 ? { ip_list_url: d.ip_list_url.trim(), ip_list_format: d.ip_list_format } : {}),
    ...(acknowledged_warnings !== undefined && acknowledged_warnings.length > 0 ? { acknowledged_warnings } : {}),
  };
}

/**
 * ADR-0230 amendment 2026-10-07 — the warnings a staff approval still has
 * to confirm, when the server refused with `warnings_not_acknowledged`.
 */
export function warningsFromError(err: unknown): AdminAgentRegistryWarning[] | null {
  if (!(err instanceof ApiError) || err.server_reason !== 'warnings_not_acknowledged') return null;
  const w = (err.body as { warnings?: unknown } | undefined)?.warnings;
  if (!Array.isArray(w)) return null;
  return w.filter(
    (x): x is AdminAgentRegistryWarning =>
      typeof x === 'object' && x !== null && typeof (x as { code?: unknown }).code === 'string' && typeof (x as { message?: unknown }).message === 'string',
  );
}

/** A specific, actionable message for a failed decision. */
export function decisionError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.kind === 'forbidden') return 'Admin role required.';
    if (err.kind === 'conflict') {
      return err.server_reason === 'agent_id_taken'
        ? 'That agent id was already approved for another proposal.'
        : 'Already decided elsewhere — refresh.';
    }
    if (err.status === 502) {
      return err.server_reason === 'kv_delete_failed'
        ? 'Revoked, but removing it from the edge registry failed. Revoke again to retry.'
        : 'Approved, but the edge registry write failed. Approve again to retry the write.';
    }
    if (err.kind === 'bad_request' && err.server_reason !== undefined) return `Refused by the server: ${err.server_reason}.`;
    if (err.kind === 'not_found') return 'Proposal not found — refresh.';
  }
  return 'The request failed. Nothing changed; retry or check the logs.';
}

/** True when the API that backs this view is not deployed yet. */
export function isApiMissing(err: unknown): boolean {
  return err instanceof ApiError && err.kind === 'not_found';
}

export function shortKey(k: string): string {
  return k.length > 12 ? `${k.slice(0, 12)}…` : k;
}
